import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DebugUiBridge } from '../DebugUiBridge';
import type { SceneGraphNode, SceneNodeDetails, EngineToDebugUiEvent, SceneGraphMaterialSummary, LightProperties, MaterialBehaviorParams } from '../types';
import { DEBUG_UI_ACTION_EVENT, type DebugUiUiAction } from '../uiActions';
import { StateManager } from '../../state/StateManager';
import { sharedStyles } from '../styles/shared-styles';
import 'xstatexray';

/**
 * Explorer Panel
 * 
 * Features:
 * - Scene graph tree visualization
 * - Entity filtering and inspection
 * - Favorites system for quick access to nodes
 * - Selection details (transforms, material, textures, entity state)
 * - Pick mode to select objects by clicking in 3D scene
 * - Search filter for scene graph
 */
@customElement('explorer-panel')
export class ExplorerPanel extends LitElement {
  static styles = [
    sharedStyles,
    css`
    :host {
      display: block;
      height: 100%;
      color: #eee;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12px;
    }

    .panel-content {
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: 12px;
      gap: 8px;
      overflow: hidden;
    }

    /* Overrides or specific styles */
    label {
      min-width: 80px;
    }

    input[type="text"] {
      flex: 1;
      font-family: ui-monospace, SFMono-Regular, monospace;
    }

    .fill-scroll {
      flex: 1;
      min-height: 0;
      overflow: auto;
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 4px;
      padding: 4px;
    }

    /* Hide scene graph from accessibility tree / Playwright snapshots */
    .scene-graph-container {
      /* Visually shows but hidden from a11y tree */
    }
    .scene-graph-container * {
      /* Remove all children from accessibility tree */
      -webkit-user-modify: read-only;
    }

    .node-row {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 6px;
      cursor: pointer;
      border-radius: 3px;
      font-size: 11px;
      transition: background 0.1s;
    }

    .node-row:hover {
      background: rgba(255, 255, 255, 0.08);
    }

    .node-row.selected {
      background: rgba(33, 150, 243, 0.25);
      border-left: 2px solid #2196F3;
    }

    .mono {
      font-family: ui-monospace, SFMono-Regular, monospace;
      font-size: 11px;
    }

    .node-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .mini-btn {
      padding: 1px 4px;
      font-size: 9px;
      opacity: 0.7;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.08);
      color: #eee;
      cursor: pointer;
    }

    .mini-btn:hover {
      opacity: 1;
      background: rgba(255, 255, 255, 0.12);
    }

    .hint {
      opacity: 0.6;
      font-style: italic;
    }

    .preview {
      margin: 8px 0;
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.3);
      padding: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100px;
    }

    .preview img {
      max-width: 100%;
      max-height: 200px;
      border-radius: 4px;
    }

    .textures {
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin-top: 6px;
    }

    .tex-row {
      display: flex;
      gap: 8px;
      align-items: center;
      padding: 4px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
    }

    .tex-thumb {
      width: 48px;
      height: 48px;
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 3px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.3);
    }

    .tex-thumb img {
      max-width: 100%;
      max-height: 100%;
    }

    .tex-meta {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .selection-details {
      display: flex;
      flex-direction: column;
      gap: 4px;
      max-height: 400px;
      min-height: 250px; /* Preallocated height */
      overflow-y: auto;
    }

    .tab-bar {
      display: flex;
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
      margin-bottom: 8px;
    }
    .tab-btn {
      padding: 6px 12px;
      background: transparent;
      border: none;
      border-bottom: 2px solid transparent;
      color: #aaa;
      cursor: pointer;
      font-size: 11px;
      font-weight: 600;
    }
    .tab-btn:hover {
      color: #eee;
      background: rgba(255, 255, 255, 0.05);
    }
    .tab-btn.active {
      color: #2196F3;
      border-bottom-color: #2196F3;
    }

    .slider-row input[type="range"] {
      flex: 1;
      height: 20px;
      cursor: pointer;
    }
  `];

  @state() private sceneGraph: SceneGraphNode[] = [];
  @state() private selectedNodeId: string | null = null;
  @state() private selectedNode: SceneNodeDetails | null = null;
  @state() private sceneSearch = '';
  @state() private pickMode = false;
  @state() private favorites: string[] = [];
  @state() private filterType: 'all' | 'entity' | 'material' = 'all';
  @state() private activeTab: 'properties' | 'material' | 'entity' | 'light' = 'properties';

  private pendingRevealNodeId: string | null = null;

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('selectedNodeId')) {
      void this.scrollSelectedNodeIntoView();
      // Reset tab when selection changes, unless current tab is valid for new selection
      if (this.selectedNode) {
          if (this.activeTab === 'entity' && !this.selectedNode.entityId) this.activeTab = 'properties';
          if (this.activeTab === 'material' && !this.selectedNode.material) this.activeTab = 'properties';
          if (this.activeTab === 'light' && !this.selectedNode.lightProperties) this.activeTab = 'properties';
      }
    }
  }

  private async scrollSelectedNodeIntoView(): Promise<void> {
    const id = this.selectedNodeId;
    if (!id) return;
    await this.updateComplete;

    const escaped =
      typeof (globalThis as any).CSS?.escape === 'function'
        ? (globalThis as any).CSS.escape(id)
        : id.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const row = this.renderRoot.querySelector<HTMLElement>(`.node-row[data-node-id="${escaped}"]`);
    if (!row) return;

    row.scrollIntoView({ block: 'nearest' });
  }

  private onUiEventBound = (e: Event) => {
    const ce = e as CustomEvent<EngineToDebugUiEvent>;
    const msg = ce.detail;
    if (!msg) return;

    if (msg.type === 'scene:graph') {
      this.sceneGraph = msg.graph;
      this.selectedNode = msg.selected;
      if (msg.selected?.id) this.selectedNodeId = msg.selected.id;

      // Migrate old id-based favorites to path-based favorites when possible
      const pathIndex = this.indexSceneGraphByPath(this.sceneGraph);
      const byId = this.indexSceneGraph(this.sceneGraph);
      const migrated = this.favorites.map((key) => {
        if (pathIndex.has(key)) return key;
        const node = byId.get(key);
        return node ? node.path : key;
      });
      if (migrated.join('|') !== this.favorites.join('|')) {
        this.favorites = migrated;
        this.saveFavorites();
      }

      void this.scrollSelectedNodeIntoView();
      if (this.pendingRevealNodeId && this.pendingRevealNodeId === this.selectedNodeId) {
        this.pendingRevealNodeId = null;
      }
    } else if (msg.type === 'scene:pick') {
      this.pickMode = false;
      if (msg.selectedNodeId) {
        this.selectedNodeId = msg.selectedNodeId;
      }
      this.refreshSceneGraph();
    }
  };

  private onUiActionBound = (e: Event) => {
    const ce = e as CustomEvent<DebugUiUiAction>;
    const action = ce.detail;
    if (!action) return;

    if (action.type === 'revealSceneNode') {
      this.sceneSearch = '';
      this.selectedNodeId = action.nodeId;
      this.refreshSceneGraph();
      this.pendingRevealNodeId = action.nodeId;
    }
  };

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('himum:debug-ui-event', this.onUiEventBound as any);
    window.addEventListener(DEBUG_UI_ACTION_EVENT, this.onUiActionBound as any);
    this.loadFavorites();
    this.refreshSceneGraph();
    DebugUiBridge.dispatchCommand({ type: 'explorer:visibility', visible: true });
  }

  disconnectedCallback(): void {
    DebugUiBridge.dispatchCommand({ type: 'explorer:visibility', visible: false });
    window.removeEventListener('himum:debug-ui-event', this.onUiEventBound as any);
    window.removeEventListener(DEBUG_UI_ACTION_EVENT, this.onUiActionBound as any);
    super.disconnectedCallback();
  }

  private loadFavorites(): void {
    try {
      const raw = localStorage.getItem('himum.explorerPanel.favorites');
      if (raw) {
        this.favorites = JSON.parse(raw);
      }
    } catch {
      // ignore
    }
  }

  private saveFavorites(): void {
    try {
      localStorage.setItem('himum.explorerPanel.favorites', JSON.stringify(this.favorites));
    } catch {
      // ignore
    }
  }

  private refreshSceneGraph(): void {
    DebugUiBridge.dispatchCommand({
      type: 'scene:graph:refresh',
      pinnedNodeIds: [],
      selectedNodeId: this.selectedNodeId,
    });
  }

  private isFavorite(key: string): boolean {
    return this.favorites.includes(key);
  }

  private toggleFavorite(key: string): void {
    if (this.favorites.includes(key)) {
      this.favorites = this.favorites.filter((id) => id !== key);
    } else {
      this.favorites = [...this.favorites, key];
    }
    this.saveFavorites();
  }

  private indexSceneGraph(nodes: SceneGraphNode[]): Map<string, SceneGraphNode> {
    const index = new Map<string, SceneGraphNode>();
    const walk = (n: SceneGraphNode) => {
      index.set(n.id, n);
      for (const c of n.children) walk(c);
    };
    for (const n of nodes) walk(n);
    return index;
  }

  private indexSceneGraphByPath(nodes: SceneGraphNode[]): Map<string, SceneGraphNode> {
    const index = new Map<string, SceneGraphNode>();
    const walk = (n: SceneGraphNode) => {
      index.set(n.path, n);
      for (const c of n.children) walk(c);
    };
    for (const n of nodes) walk(n);
    return index;
  }

  private selectNode(nodeId: string): void {
    this.selectedNodeId = nodeId;
    this.refreshSceneGraph();
  }

  private clearSelection(): void {
    this.selectedNodeId = null;
    this.selectedNode = null;
    this.refreshSceneGraph();
  }

  private setPickMode(enabled: boolean): void {
    this.pickMode = enabled;
    DebugUiBridge.dispatchCommand({ type: 'scene:pickMode:set', enabled });
  }

  private highlightNode(nodeId: string | null): void {
    DebugUiBridge.dispatchCommand({ type: 'scene:highlight', nodeId });
  }

  private nodeMatchesSearch(node: SceneGraphNode, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;

    const parts: string[] = [node.name, node.type];
    if (node.material) {
      parts.push(node.material.className);
      if (node.material.name) parts.push(node.material.name);
      if (node.material.effectName) parts.push(node.material.effectName);
    }

    const hay = parts.filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  }

  private filterSceneTree(nodes: SceneGraphNode[], query: string): SceneGraphNode[] {
    const q = query.trim();
    
    const walk = (n: SceneGraphNode): SceneGraphNode | null => {
      const children = n.children.map(walk).filter((c): c is SceneGraphNode => !!c);
      
      let matchesType = true;
      if (this.filterType === 'entity') {
        matchesType = !!n.entityId;
      } else if (this.filterType === 'material') {
        matchesType = !!n.material;
      }

      const selfMatch = matchesType && this.nodeMatchesSearch(n, q);
      
      if (selfMatch || children.length) {
        return { ...n, children };
      }
      return null;
    };

    return nodes.map(walk).filter((n): n is SceneGraphNode => !!n);
  }

  private renderSceneNode(node: SceneGraphNode, depth: number): unknown {
    const pad = `${depth * 12}px`;
    const hasMaterial = node.material ? true : false;
    const isEntity = !!node.entityId;
    const selected = this.selectedNodeId === node.id;
    const favKey = node.path;
    const fav = this.isFavorite(favKey);

    return html`
      <div
        class="node-row ${selected ? 'selected' : ''}"
        data-node-id=${node.id}
        style="padding-left:${pad}"
        @click=${() => this.selectNode(node.id)}
        @mouseenter=${() => this.highlightNode(node.id)}
        @mouseleave=${() => this.highlightNode(null)}
        title=${node.name}
        aria-hidden="true"
      >
        <span class="pill">${node.type}</span>
        <span class="mono node-name" title=${node.name}>${node.name || '(unnamed)'}</span>
        <button
          class="mini-btn"
          @click=${(e: Event) => {
            e.stopPropagation();
            this.toggleFavorite(favKey);
          }}
          title=${fav ? 'Remove favorite' : 'Add favorite'}
        >
          ${fav ? 'unfav' : 'fav'}
        </button>
        ${isEntity ? html`<span class="pill ok">entity</span>` : nothing}
        ${hasMaterial
          ? html`<span class="pill ok">mat</span
              ><span class="pill">${node.material?.className ?? 'Material'}</span>`
          : html`<span class="pill danger">no mat</span>`}
      </div>
      ${node.children.map((c) => this.renderSceneNode(c, depth + 1))}
    `;
  }

  private renderEntityPanel(entityId: string) {
    const sm = StateManager.getInstance();
    const actor = sm.getCharacterActor(entityId) || sm.getDynamicObjectActor(entityId);

    return html`
      <div class="row">
        <label>Entity ID</label>
        <span class="mono">${entityId}</span>
        <button @click=${() => this.selectEntityInStatePanel(entityId)}>Open in State Panel</button>
      </div>

      ${actor ? html`
        <div class="section-title" style="margin-top: 12px;">State Machine</div>
        <div style="height: 300px; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; overflow: hidden; background: rgba(0,0,0,0.2);">
          <xstate-visualizer
            .interpreter=${actor}
            style="height: 100%; width: 100%; --visualizer-bg: transparent;"
          ></xstate-visualizer>
        </div>
      ` : html`
        <div class="mono hint" style="margin-top: 4px; opacity: 0.5;">No active state machine</div>
      `}
    `;
  }

  private selectEntityInStatePanel(entityId: string) {
    window.dispatchEvent(new CustomEvent('himum:select-actor', { detail: { actorId: entityId } }));
  }

  private renderPropertiesTab(s: SceneNodeDetails) {
      return html`
        <div class="row">
            <label>Name</label>
            <span class="mono">${s.name || '(unnamed)'}</span>
        </div>
        <div class="row"><label>Type</label><span class="mono">${s.type}</span></div>
        <div class="row"><label>ID</label><span class="mono">${s.id}</span></div>
        <div class="row">
            <label>Visible</label>
            <span class="mono">${s.isVisible == null ? '—' : s.isVisible ? '✓' : '✗'}</span>
        </div>
        <div class="row">
            <label>Enabled</label>
            <span class="mono">${s.enabled == null ? '—' : s.enabled ? '✓' : '✗'}</span>
        </div>
        
        ${s.properties ? Object.entries(s.properties).map(([key, value]) => html`
            <div class="row">
                <label>${key}</label>
                <span class="mono">${value}</span>
            </div>
        `) : nothing}

        <div class="divider"></div>

        <div class="row">
            <label>Position</label>
            <span class="mono">
            ${s.position
                ? `(${s.position.x.toFixed(2)}, ${s.position.y.toFixed(2)}, ${s.position.z.toFixed(2)})`
                : '—'}
            </span>
        </div>
        <div class="row">
            <label>Global Pos</label>
            <span class="mono">
            ${s.absolutePosition
                ? `(${s.absolutePosition.x.toFixed(2)}, ${s.absolutePosition.y.toFixed(2)}, ${s.absolutePosition.z.toFixed(2)})`
                : '—'}
            </span>
        </div>
        <div class="row">
            <label>Rotation</label>
            <span class="mono">
            ${s.rotation
                ? `(${s.rotation.x.toFixed(2)}, ${s.rotation.y.toFixed(2)}, ${s.rotation.z.toFixed(2)})`
                : '—'}
            </span>
        </div>
        <div class="row">
            <label>Scaling</label>
            <span class="mono">
            ${s.scaling
                ? `(${s.scaling.x.toFixed(2)}, ${s.scaling.y.toFixed(2)}, ${s.scaling.z.toFixed(2)})`
                : '—'}
            </span>
        </div>
        <div class="row"><label>Vertices</label><span class="mono">${s.vertices ?? '—'}</span></div>
        <div class="row"><label>Indices</label><span class="mono">${s.indices ?? '—'}</span></div>
      `;
  }

  private renderMaterialTab(
    meshName: string,
    material: SceneGraphMaterialSummary,
    textures: any[],
    godRaysParams?: { exposure: number; decay: number; weight: number; density: number },
    materialBehavior?: MaterialBehaviorParams
  ) {
      return html`
        <div class="row">
            <label>Material</label>
            <span class="mono">
            <span class="pill ok">mat</span>
            ${material.className}${material.name ? `: ${material.name}` : ''}
            </span>
        </div>
        <div class="row">
            <label>Shader</label>
            <span class="mono">${material.effectName ?? '—'}</span>
        </div>

        <div class="preview">
        ${material.materialPreviewUrl
            ? html`<img alt="material preview" src=${material.materialPreviewUrl} />`
            : material.previewTextureUrl
            ? html`<img alt="material preview" src=${material.previewTextureUrl} />`
            : html`<span class="mono">No material preview</span>`}
        </div>

        ${textures.length
            ? html`
                <div class="section-title">Textures</div>
                <div class="textures">
                ${textures.map((t) => {
                    const url = t.previewUrl ?? t.url;
                    return html`
                    <div class="tex-row" title=${t.url ?? ''}>
                        <div class="tex-thumb">${url ? html`<img alt=${t.slot} src=${url} />` : nothing}</div>
                        <div class="tex-meta">
                        <div class="mono"><span class="pill">${t.slot}</span></div>
                        <div class="mono">${t.name ?? '(unnamed)'}</div>
                        </div>
                    </div>
                    `;
                })}
                </div>
            `
            : nothing}

        ${materialBehavior ? this.renderMaterialBehaviorSection(materialBehavior) : nothing}
        ${godRaysParams ? this.renderGodRaysSection(meshName, godRaysParams) : nothing}
      `;
  }

  private renderMaterialBehaviorSection(materialBehavior: MaterialBehaviorParams) {
    const { behaviorId, displayName, entityId, params } = materialBehavior;
    
    // Get param metadata for rendering appropriate controls
    const paramMeta = this.getMaterialParamMeta(behaviorId);
    
    return html`
      <div class="section-title">${displayName}</div>
      ${Object.entries(params).map(([key, value]) => {
        const meta = paramMeta[key] || {};
        const min = meta.min ?? 0;
        const max = meta.max ?? (typeof value === 'number' ? Math.max(10, value * 2) : 1);
        const step = meta.step ?? 0.01;
        
        if (typeof value === 'number') {
          return html`
            <div class="row slider-row">
              <label title=${meta.description || key}>${meta.label || key}</label>
              <input
                type="range"
                min=${min}
                max=${max}
                step=${step}
                .value=${String(value)}
                @input=${(e: Event) => {
                  const newValue = parseFloat((e.target as HTMLInputElement).value);
                  DebugUiBridge.dispatchCommand({
                    type: 'material:behavior:param:set',
                    entityId,
                    paramName: key,
                    value: newValue
                  });
                }}
              />
              <span class="mono">${value.toFixed(step < 0.01 ? 3 : 2)}</span>
            </div>
          `;
        } else if (typeof value === 'boolean') {
          return html`
            <div class="row">
              <label title=${meta.description || key}>${meta.label || key}</label>
              <input
                type="checkbox"
                ?checked=${value}
                @change=${(e: Event) => {
                  const newValue = (e.target as HTMLInputElement).checked;
                  DebugUiBridge.dispatchCommand({
                    type: 'material:behavior:param:set',
                    entityId,
                    paramName: key,
                    value: newValue
                  });
                }}
              />
            </div>
          `;
        } else {
          // String or other - display as read-only
          return html`
            <div class="row">
              <label title=${meta.description || key}>${meta.label || key}</label>
              <span class="mono">${String(value)}</span>
            </div>
          `;
        }
      })}
    `;
  }

  private getMaterialParamMeta(behaviorId: string): Record<string, { label?: string; min?: number; max?: number; step?: number; description?: string }> {
    // Metadata for material behavior parameters
    switch (behaviorId) {
      case 'material:water':
        return {
          windForce: { label: 'Wind Force', min: 0, max: 20, step: 0.1, description: 'Force of wind affecting waves' },
          waveHeight: { label: 'Wave Height', min: 0, max: 2, step: 0.01, description: 'Height of waves' },
          bumpHeight: { label: 'Bump Height', min: 0, max: 2, step: 0.01, description: 'Normal map intensity' },
          windDirectionX: { label: 'Wind Dir X', min: -1, max: 1, step: 0.01, description: 'Wind direction X component' },
          windDirectionY: { label: 'Wind Dir Y', min: -1, max: 1, step: 0.01, description: 'Wind direction Y component' },
          waterColorR: { label: 'Color R', min: 0, max: 1, step: 0.01, description: 'Water color red' },
          waterColorG: { label: 'Color G', min: 0, max: 1, step: 0.01, description: 'Water color green' },
          waterColorB: { label: 'Color B', min: 0, max: 1, step: 0.01, description: 'Water color blue' },
          colorBlendFactor: { label: 'Color Blend', min: 0, max: 1, step: 0.01, description: 'How much water color blends' },
          waveLength: { label: 'Wave Length', min: 0, max: 1, step: 0.01, description: 'Length of waves' },
        };
      case 'material:lava':
        return {
          flow_speed: { label: 'Flow Speed', min: 0, max: 2, step: 0.01, description: 'Speed of lava flow' },
          flow_direction_x: { label: 'Flow Dir X', min: -1, max: 1, step: 0.01, description: 'Flow direction X' },
          flow_direction_y: { label: 'Flow Dir Y', min: -1, max: 1, step: 0.01, description: 'Flow direction Y' },
          emissive_intensity: { label: 'Emissive', min: 0, max: 5, step: 0.1, description: 'Glow intensity' },
          distortion_scale: { label: 'Distortion', min: 0, max: 0.2, step: 0.005, description: 'UV distortion amount' },
          distortion_frequency: { label: 'Distort Freq', min: 0, max: 20, step: 0.5, description: 'Distortion frequency' },
        };
      case 'material:video':
        return {
          emissive_intensity: { label: 'Emissive', min: 0, max: 5, step: 0.1, description: 'Glow intensity' },
          src: { label: 'Source', description: 'Video file path' },
          autoplay: { label: 'Autoplay', description: 'Auto-start playback' },
          loop: { label: 'Loop', description: 'Loop video' },
        };
      default:
        return {};
    }
  }

  private renderGodRaysSection(
    meshName: string,
    params: { exposure: number; decay: number; weight: number; density: number }
  ) {
    return html`
      <div class="section-title">God Rays</div>
      <div class="row slider-row">
        <label>Exposure</label>
        <input
          type="range"
          min="0"
          max="2"
          step="0.01"
          .value=${String(params.exposure)}
          @input=${(e: Event) => {
            const value = parseFloat((e.target as HTMLInputElement).value);
            DebugUiBridge.dispatchCommand({
              type: 'postprocess:meshGodRays:exposure:set',
              meshName,
              value
            });
          }}
        />
        <span class="mono">${params.exposure.toFixed(2)}</span>
      </div>
      <div class="row slider-row">
        <label>Decay</label>
        <input
          type="range"
          min="0.8"
          max="1"
          step="0.001"
          .value=${String(params.decay)}
          @input=${(e: Event) => {
            const value = parseFloat((e.target as HTMLInputElement).value);
            DebugUiBridge.dispatchCommand({
              type: 'postprocess:meshGodRays:decay:set',
              meshName,
              value
            });
          }}
        />
        <span class="mono">${params.decay.toFixed(3)}</span>
      </div>
      <div class="row slider-row">
        <label>Weight</label>
        <input
          type="range"
          min="0"
          max="2"
          step="0.01"
          .value=${String(params.weight)}
          @input=${(e: Event) => {
            const value = parseFloat((e.target as HTMLInputElement).value);
            DebugUiBridge.dispatchCommand({
              type: 'postprocess:meshGodRays:weight:set',
              meshName,
              value
            });
          }}
        />
        <span class="mono">${params.weight.toFixed(2)}</span>
      </div>
      <div class="row slider-row">
        <label>Density</label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          .value=${String(params.density)}
          @input=${(e: Event) => {
            const value = parseFloat((e.target as HTMLInputElement).value);
            DebugUiBridge.dispatchCommand({
              type: 'postprocess:meshGodRays:density:set',
              meshName,
              value
            });
          }}
        />
        <span class="mono">${params.density.toFixed(2)}</span>
      </div>
    `;
  }

  private updateLightProperty(nodeId: string, property: keyof LightProperties, value: number | boolean | { r: number; g: number; b: number }) {
    DebugUiBridge.dispatchCommand({ type: 'light:update', nodeId, property, value });
  }

  private rgbToHex(r: number, g: number, b: number): string {
    const toHex = (c: number) => Math.round(c * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16) / 255,
      g: parseInt(result[2], 16) / 255,
      b: parseInt(result[3], 16) / 255
    } : { r: 1, g: 1, b: 1 };
  }

  private renderLightTab(nodeId: string, light: LightProperties) {
    const isSpotLight = light.lightType === 'SpotLight';
    const hasRange = light.range !== undefined;
    const diffuseHex = this.rgbToHex(light.diffuseColor.r, light.diffuseColor.g, light.diffuseColor.b);
    const specularHex = this.rgbToHex(light.specularColor.r, light.specularColor.g, light.specularColor.b);

    return html`
      <div class="row">
        <label>Type</label>
        <span class="mono"><span class="pill ok">${light.lightType}</span></span>
      </div>

      <div class="row">
        <label>Enabled</label>
        <input
          type="checkbox"
          .checked=${light.enabled}
          @change=${(e: Event) => this.updateLightProperty(nodeId, 'enabled', (e.target as HTMLInputElement).checked)}
        />
        <span class="mono">${light.enabled ? 'On' : 'Off'}</span>
      </div>

      <div class="divider"></div>

      <div class="row">
        <label>Intensity</label>
        <input
          type="range"
          min="0"
          max="100000"
          step="100"
          .value=${String(light.intensity)}
          @input=${(e: Event) => this.updateLightProperty(nodeId, 'intensity', Number((e.target as HTMLInputElement).value))}
          style="flex: 1;"
        />
        <input
          type="number"
          .value=${String(light.intensity)}
          @change=${(e: Event) => this.updateLightProperty(nodeId, 'intensity', Number((e.target as HTMLInputElement).value))}
          style="width: 70px;"
        />
      </div>

      <div class="row">
        <label>Diffuse</label>
        <input
          type="color"
          .value=${diffuseHex}
          @input=${(e: Event) => this.updateLightProperty(nodeId, 'diffuseColor', this.hexToRgb((e.target as HTMLInputElement).value))}
        />
        <span class="mono">${diffuseHex}</span>
      </div>

      <div class="row">
        <label>Specular</label>
        <input
          type="color"
          .value=${specularHex}
          @input=${(e: Event) => this.updateLightProperty(nodeId, 'specularColor', this.hexToRgb((e.target as HTMLInputElement).value))}
        />
        <span class="mono">${specularHex}</span>
      </div>

      ${hasRange ? html`
        <div class="divider"></div>
        <div class="row">
          <label>Range</label>
          <input
            type="range"
            min="0"
            max="500"
            step="1"
            .value=${String(light.range)}
            @input=${(e: Event) => this.updateLightProperty(nodeId, 'range', Number((e.target as HTMLInputElement).value))}
            style="flex: 1;"
          />
          <input
            type="number"
            .value=${String(light.range)}
            @change=${(e: Event) => this.updateLightProperty(nodeId, 'range', Number((e.target as HTMLInputElement).value))}
            style="width: 70px;"
          />
        </div>
      ` : nothing}

      ${isSpotLight ? html`
        <div class="divider"></div>
        <div class="section-title">Spotlight Settings</div>

        <div class="row">
          <label>Angle</label>
          <input
            type="range"
            min="0"
            max="${Math.PI}"
            step="0.01"
            .value=${String(light.angle ?? 0)}
            @input=${(e: Event) => this.updateLightProperty(nodeId, 'angle', Number((e.target as HTMLInputElement).value))}
            style="flex: 1;"
          />
          <span class="mono">${((light.angle ?? 0) * 180 / Math.PI).toFixed(1)}°</span>
        </div>

        <div class="row">
          <label>Inner Angle</label>
          <input
            type="range"
            min="0"
            max="${Math.PI}"
            step="0.01"
            .value=${String(light.innerAngle ?? 0)}
            @input=${(e: Event) => this.updateLightProperty(nodeId, 'innerAngle', Number((e.target as HTMLInputElement).value))}
            style="flex: 1;"
          />
          <span class="mono">${((light.innerAngle ?? 0) * 180 / Math.PI).toFixed(1)}°</span>
        </div>

        <div class="row">
          <label>Exponent</label>
          <input
            type="range"
            min="0"
            max="10"
            step="0.1"
            .value=${String(light.exponent ?? 2)}
            @input=${(e: Event) => this.updateLightProperty(nodeId, 'exponent', Number((e.target as HTMLInputElement).value))}
            style="flex: 1;"
          />
          <span class="mono">${(light.exponent ?? 2).toFixed(1)}</span>
        </div>
      ` : nothing}

      <div class="divider"></div>
      <div class="row">
        <label>Shadows</label>
        <span class="mono">${light.shadowEnabled ? html`<span class="pill ok">Enabled</span>` : html`<span class="pill danger">Disabled</span>`}</span>
        ${light.shadowMapSize ? html`<span class="mono">(${light.shadowMapSize}x${light.shadowMapSize})</span>` : nothing}
      </div>
    `;
  }

  render() {
    const filtered = this.filterSceneTree(this.sceneGraph, this.sceneSearch);
    const nodeIndex = this.indexSceneGraph(this.sceneGraph);
    const pathIndex = this.indexSceneGraphByPath(this.sceneGraph);

    const favoriteNodes = this.favorites.map((key) => ({
      key,
      node: pathIndex.get(key) ?? nodeIndex.get(key) ?? null,
    }));

    const s = this.selectedNode;
    const material = s?.material ?? null;
    const textures = material?.textures ?? [];

    return html`
      <div class="panel-content">
        <div class="row">
          <button @click=${() => this.refreshSceneGraph()}>Refresh</button>
          <button
            class=${this.pickMode ? 'active' : ''}
            @click=${() => this.setPickMode(!this.pickMode)}
            title="Toggle pick mode: click in the scene to select"
          >
            ${this.pickMode ? 'Picking…' : 'Pick'}
          </button>
          <select
            @change=${(e: Event) => this.filterType = (e.target as HTMLSelectElement).value as any}
            .value=${this.filterType}
          >
            <option value="all">All Nodes</option>
            <option value="entity">Entities</option>
            <option value="material">With Material</option>
          </select>
          <label>Search</label>
          <input
            type="text"
            .value=${this.sceneSearch}
            @input=${(e: Event) => {
              this.sceneSearch = (e.target as HTMLInputElement).value;
            }}
          />
          <button @click=${() => this.clearSelection()}>Clear Selection</button>
        </div>

        <div class="divider"></div>

        <div class="section-title">Favorites</div>
        ${favoriteNodes.length
          ? html`${favoriteNodes.map(({ key, node }) => {
              const type = node?.type ?? 'Unknown';
              const name = node?.name ?? key;
              return html`
                <div class="node-row" @click=${() => node && this.selectNode(node.id)} title=${key}>
                  <span class="pill">${type}</span>
                  <span class="mono node-name" title=${key}>${name}</span>
                  <button
                    class="mini-btn"
                    @click=${(e: Event) => {
                      e.stopPropagation();
                      this.toggleFavorite(key);
                    }}
                    title="Remove favorite"
                  >
                    unfav
                  </button>
                </div>
              `;
            })}`
          : html`<div class="mono">No favorites yet.</div>`}

        <div class="divider"></div>

        <div class="section-title">Selection</div>
        ${this.pickMode ? html`<div class="mono hint">Pick mode enabled: click an object in the scene.</div>` : nothing}
        ${s
          ? html`
              <div class="selection-details">
                <div class="tab-bar">
                    <button class="tab-btn ${this.activeTab === 'properties' ? 'active' : ''}" @click=${() => this.activeTab = 'properties'}>Properties</button>
                    ${s.entityId ? html`<button class="tab-btn ${this.activeTab === 'entity' ? 'active' : ''}" @click=${() => this.activeTab = 'entity'}>Entity</button>` : nothing}
                    ${material ? html`<button class="tab-btn ${this.activeTab === 'material' ? 'active' : ''}" @click=${() => this.activeTab = 'material'}>Material</button>` : nothing}
                    ${s.lightProperties ? html`<button class="tab-btn ${this.activeTab === 'light' ? 'active' : ''}" @click=${() => this.activeTab = 'light'}>Light</button>` : nothing}
                </div>

                ${this.activeTab === 'properties' ? this.renderPropertiesTab(s) : nothing}
                ${this.activeTab === 'entity' && s.entityId ? this.renderEntityPanel(s.entityId) : nothing}
                ${this.activeTab === 'material' && material ? this.renderMaterialTab(s.name, material, textures, s.godRaysParams, s.materialBehavior) : nothing}
                ${this.activeTab === 'light' && s.lightProperties ? this.renderLightTab(s.id, s.lightProperties) : nothing}
              </div>
            `
          : html`<div class="mono">No node selected.</div>`}

        <div class="divider"></div>

        <div class="section-title">Scene Graph <span class="mono hint">(${filtered.length} nodes)</span></div>
        <div class="fill-scroll scene-graph-container" role="none" aria-hidden="true">
          ${filtered.length
            ? html`${filtered.map((n) => this.renderSceneNode(n, 0))}`
            : html`<div class="mono">No scene graph loaded yet.</div>`}
        </div>
      </div>
    `;
  }
}
